import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LayoutGrid, Plus, Loader2, Sparkles, Home, Trophy,
  Image as ImageIcon, Layers, ArrowRight, ArrowLeft, Calendar
} from 'lucide-react';
import { CarouselEditor } from '../components/editor/carousel/CarouselEditor';
import apiClient from '../utils/api';
import { MobileTabBar } from '../components/MobileTabBar';
import { AppNavbar } from '../components/AppNavbar';
import { Button } from '../components/ui/button';

const ICONS = { sparkles: Sparkles, home: Home, trophy: Trophy, grid: LayoutGrid };

// Minimalist Canva-style top toolbar wrapping the carousel editor.
const CarouselEditorShell = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="carousel-editor-page">
      <header
        className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-gray-200 flex items-center justify-between px-3 sm:px-4 h-14"
        style={{ fontFamily: 'Manrope, sans-serif' }}
      >
        <div className="flex items-center gap-1 sm:gap-2 min-w-0">
          <button
            onClick={() => navigate('/dashboard')}
            className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-gray-100 active:scale-95 transition-all"
            data-testid="carousel-editor-home"
            aria-label="Dashboard"
          >
            <Home className="h-[18px] w-[18px] text-gray-700" />
          </button>
          <button
            onClick={() => navigate('/carousel-editor')}
            className="h-10 px-3 -ml-1 flex items-center gap-1.5 rounded-full hover:bg-gray-100 active:scale-95 transition-all text-sm text-gray-700"
            data-testid="back-to-carousels"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Templates</span>
          </button>
          <span
            className="ml-2 hidden sm:inline text-sm font-semibold tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Carousel Maker
          </span>
        </div>
        <button
          onClick={() => navigate('/scheduled-posts')}
          className="h-10 px-3 inline-flex items-center gap-1.5 rounded-full hover:bg-gray-100 active:scale-95 transition-all text-sm text-gray-700"
          data-testid="carousel-editor-schedule"
          title="Scheduled posts"
        >
          <Calendar className="h-[18px] w-[18px]" />
          <span className="hidden sm:inline">Schedule</span>
        </button>
      </header>
      <div className="flex-1">
        <CarouselEditor />
      </div>
    </div>
  );
};

const CarouselEditorPage = () => {
  const [searchParams] = useSearchParams();
  const compId = searchParams.get('id');
  const propertyParam = searchParams.get('property');

  // If we have a comp ID, property data, or id=new, show the editor
  if (compId || propertyParam) {
    return <CarouselEditorShell />;
  }

  // Otherwise show the template picker
  return <CarouselTemplatePicker />;
};

const CarouselTemplatePicker = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get('/carousel/templates');
        setTemplates(res.data.templates || []);
      } catch {
        toast.error('Failed to load templates');
      }
      setLoading(false);
    })();
  }, []);

  const handleSelectTemplate = (tpl) => {
    navigate(`/carousel-editor?id=new`, {
      state: { carouselTemplate: tpl }
    });
  };

  return (
    <div
      className="min-h-screen bg-white pb-24 md:pb-0"
      style={{ fontFamily: 'Manrope, sans-serif' }}
      data-testid="carousel-template-picker"
    >
      <AppNavbar />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="mb-8 sm:mb-10 flex items-end justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400">Multi-slide</p>
            <h2
              className="mt-1 text-3xl sm:text-4xl font-light leading-[1.05] tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Pick a <span style={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic' }}>carousel</span>.
            </h2>
            <p className="mt-2 text-sm text-gray-400">Choose a layout or start from a blank canvas.</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/my-carousels')}
              className="rounded-full"
              data-testid="view-saved-carousels"
            >
              <Layers className="h-3.5 w-3.5 mr-1.5" /> My Carousels
            </Button>
            <Button
              size="sm"
              onClick={() => navigate('/carousel-editor?id=new')}
              className="rounded-full bg-[#0B0C10] hover:bg-gray-900"
              data-testid="blank-carousel-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Blank Carousel
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-2xl bg-gray-50 border border-dashed border-gray-200 flex items-center justify-center mx-auto mb-5">
              <LayoutGrid className="h-7 w-7 text-gray-300" />
            </div>
            <p className="text-sm text-gray-500 mb-4">No carousel templates available yet</p>
            <button
              onClick={() => navigate('/carousel-editor?id=new')}
              className="h-10 px-5 inline-flex items-center gap-1.5 rounded-full bg-[#0B0C10] text-white text-sm font-medium hover:bg-gray-900 transition-colors"
            >
              <Plus className="h-4 w-4" /> Start with Blank Carousel
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
            {templates.map(tpl => {
              const IconComp = ICONS[tpl.icon] || Sparkles;
              const slides = tpl.slides || [];
              return (
                <button
                  key={tpl.template_id}
                  onClick={() => handleSelectTemplate(tpl)}
                  className="group text-left rounded-2xl border border-gray-200 bg-white hover:border-gray-400 hover:shadow-md transition-all overflow-hidden active:scale-[0.98]"
                  data-testid={`carousel-tpl-pick-${tpl.template_id}`}
                >
                  {/* Preview */}
                  <div className="relative h-36 bg-gray-50">
                    <div className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: tpl.color || '#0B0C10' }} />
                    <div className="w-full h-full flex items-center justify-center gap-1.5 px-4">
                      {slides.length > 0 ? slides.slice(0, 5).map((s, i) => (
                        <div
                          key={i}
                          className="flex-1 h-24 rounded-md border border-gray-200 flex items-center justify-center text-[9px] text-gray-400"
                          style={{ backgroundColor: s.background_color || '#0B0C10', color: '#fff' }}
                        >
                          {s.background_image ? <ImageIcon className="h-4 w-4 opacity-30" /> : <span className="opacity-50">S{i + 1}</span>}
                        </div>
                      )) : (
                        <IconComp className="h-12 w-12 opacity-20 text-gray-400" />
                      )}
                    </div>
                  </div>
                  {/* Info */}
                  <div className="p-4">
                    <div className="flex items-start gap-2.5">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-[#0B0C10] text-white"
                      >
                        <IconComp className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {tpl.name}
                        </h3>
                        <p className="text-[11px] text-gray-400 line-clamp-1 mt-0.5">{tpl.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="flex items-center gap-1 text-[11px] text-gray-400">
                        <Layers className="h-3 w-3" /> {slides.length} slides
                      </span>
                      <span className="text-[11px] font-medium text-gray-500 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        Use <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>

      <MobileTabBar />
    </div>
  );
};

export default CarouselEditorPage;
